import { FC } from "react";
import {
    Card,
    Tag,
} from "../../../design-system";
import { useNavigate } from "react-router-dom";

import { capitalize } from "../../../common/String";
import {
    AnalysisType,
    ProjectSummary,
    ProjectAction,
    canDo,
    isRfxProject,
    getProjectTypeLabel,
    getStatus,
} from "../../../types/ProjectTypes";
import { reassignProjectOwner } from "../../../common/api/ProjectClient";
import { DateTime } from "../../../common/components/DateTime";
import { OwnerCell } from "../../../common/components/OwnerCell";
import { ProjectNameWidget } from "./ProjectNameWidget";
import { VendorLogo } from "./VendorLogo";
import styles from "./ProjectCard.module.less";


function getSummaryAnalysisLabel(summary: ProjectSummary): string {
    const la = summary.latestAnalysis;
    if (!la) return "–";
    switch (la.type) {
        case AnalysisType.ROI: return "ROI report";
        case AnalysisType.COMPLETE: return "Complete";
        default: return `Playbook v${la.version}`;
    }
}


interface ProjectSummaryCardProps {
    project: ProjectSummary;
    onUpdate: () => void;
}

export const ProjectSummaryCard: FC<ProjectSummaryCardProps> = ({
    project,
    onUpdate,
}) => {
    const navigate = useNavigate();

    const vendorName = project.vendorName || project.vendorKey || "";
    const projectStatus = getStatus(project);

    return (
        <Card
            className={`${styles.headerCard} ${styles.headerCardWithLogo} ${styles.clickable} ${isRfxProject(project) ? styles.headerCardRfx : ""}`}
            onClick={() => navigate(`/project/${project.id}`)}
        >
            <VendorLogo vendorHero={project.vendorHero} vendorName={vendorName} projectName={project.name} />
            <div className={styles.headerContent}>
                <div className={styles.fieldRow}>
                    <div className={styles.primaryField}>
                        <div className={styles.fieldLabel}>
                            {capitalize(getProjectTypeLabel(project.type))}
                        </div>
                        <div className={styles.fieldValue}>
                            <ProjectNameWidget
                                projectId={project.id}
                                name={project.name}
                                vendorName={vendorName || undefined}
                                supplierId={project.supplierId}
                                canEdit={canDo(project, ProjectAction.EDIT)}
                                onUpdate={onUpdate}
                            />
                        </div>
                    </div>

                    <div className={styles.field}>
                        <div className={styles.fieldLabel}>Analysis</div>
                        <div className={styles.fieldValue}>
                            <Tag>{getSummaryAnalysisLabel(project)}</Tag>
                        </div>
                    </div>

                    <div className={styles.field}>
                        <div className={styles.fieldLabel}>Status</div>
                        <div className={styles.fieldValue}>
                            <Tag color={projectStatus.color}>{projectStatus.label}</Tag>
                        </div>
                    </div>

                    <div className={styles.field}>
                        <div className={styles.fieldLabel}>Documents</div>
                        <div className={styles.fieldValue}>
                            {project.documentCount}
                        </div>
                    </div>

                    <div className={styles.field}>
                        <div className={styles.fieldLabel}>Last update</div>
                        <div className={styles.fieldValue}>
                            <DateTime date={project.lastUpdateDate} showTime={false} />
                        </div>
                    </div>

                    <div className={styles.field}>
                        <div className={styles.fieldLabel}>Assigned to</div>
                        <div className={styles.fieldValue}>
                            <div onClick={(e) => e.stopPropagation()}>
                                <OwnerCell
                                    ownerId={project.ownerId}
                                    ownerName={project.ownerName}
                                    canEdit={canDo(project, ProjectAction.REASSIGN)}
                                    onSave={async (newOwnerId) => {
                                        await reassignProjectOwner(project.id, newOwnerId);
                                    }}
                                    onUpdate={onUpdate}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Card>
    );
};
